/**
 * External-video-URL preview flow for VideoSelectorModal's "External URL"
 * tab: platform detection, oEmbed metadata fetch (YouTube/Vimeo — no API
 * keys needed), URL-derived fallbacks, and stale-response guarding so an
 * earlier slow preview can never overwrite a newer one.
 */
import { useCallback, useRef, useState } from 'react';

export interface UrlPreviewData {
  platform: string;
  domain: string;
  title: string;
  thumbnailUrl?: string;
  duration?: number;
}

function detectPlatform(url: string): { platform: string; domain: string } | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '');

    if (/youtube\.com|youtu\.be/.test(host)) return { platform: 'YouTube', domain: host };
    if (/vimeo\.com/.test(host)) return { platform: 'Vimeo', domain: host };
    if (/\.(mp4|webm|ogg)$/i.test(parsed.pathname)) return { platform: 'MP4', domain: host };
    if (/\.(m3u8)$/i.test(parsed.pathname)) return { platform: 'HLS', domain: host };
    if (/\.(mpd)$/i.test(parsed.pathname)) return { platform: 'DASH', domain: host };

    return { platform: 'Video', domain: host };
  } catch {
    return null;
  }
}

/**
 * Fetch video metadata via free oEmbed APIs (no API keys needed).
 * YouTube: https://www.youtube.com/oembed?url=VIDEO_URL&format=json
 * Vimeo:   https://vimeo.com/api/oembed.json?url=VIDEO_URL
 */
async function fetchOEmbedMetadata(
  videoUrl: string,
  platform: string
): Promise<{ title?: string; thumbnailUrl?: string; duration?: number }> {
  try {
    if (platform === 'YouTube') {
      const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(videoUrl)}&format=json`;
      const res = await fetch(oembedUrl);
      if (!res.ok) return {};
      const data = await res.json();
      return {
        title: data.title,
        thumbnailUrl: data.thumbnail_url,
      };
    }

    if (platform === 'Vimeo') {
      const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(videoUrl)}`;
      const res = await fetch(oembedUrl);
      if (!res.ok) return {};
      const data = await res.json();
      return {
        title: data.title,
        thumbnailUrl: data.thumbnail_url,
        duration: data.duration,
      };
    }
  } catch {
    // oEmbed fetch failed — fall back to URL-derived info
  }

  return {};
}

function fallbackTitleFromUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    if (last) {
      return decodeURIComponent(last.replace(/[-_]/g, ' ').replace(/\.\w+$/, ''));
    }
    return 'Video';
  } catch {
    return 'Video';
  }
}

export interface VideoUrlPreviewState {
  externalUrl: string;
  externalUrlError: string | null;
  urlPreview: UrlPreviewData | null;
  isPreviewLoading: boolean;
  /** Updates the URL and clears any now-stale error/preview. */
  handleUrlChange: (url: string) => void;
  /** Validates the URL and loads preview metadata. */
  handlePreview: () => Promise<void>;
  setExternalUrlError: (error: string | null) => void;
  /** Clears all external-URL state (e.g. when the modal closes). */
  reset: () => void;
}

export function useVideoUrlPreview(): VideoUrlPreviewState {
  const [externalUrl, setExternalUrl] = useState('');
  const [externalUrlError, setExternalUrlError] = useState<string | null>(null);
  const [urlPreview, setUrlPreview] = useState<UrlPreviewData | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const previewRequestIdRef = useRef(0);

  const handleUrlChange = useCallback((url: string) => {
    setExternalUrl(url);
    setExternalUrlError(null);
    setUrlPreview(null);
  }, []);

  const handlePreview = useCallback(async () => {
    const trimmed = externalUrl.trim();
    if (!trimmed) {
      setExternalUrlError('Please enter a video URL.');
      setUrlPreview(null);
      return;
    }

    const detected = detectPlatform(trimmed);
    if (!detected) {
      setExternalUrlError('Please enter a valid URL.');
      setUrlPreview(null);
      return;
    }

    setExternalUrlError(null);
    setIsPreviewLoading(true);
    const requestId = ++previewRequestIdRef.current;

    // Start with URL-derived fallback
    const preview: UrlPreviewData = {
      ...detected,
      title: fallbackTitleFromUrl(trimmed),
    };

    // Fetch real metadata from oEmbed (YouTube / Vimeo)
    const oembed = await fetchOEmbedMetadata(trimmed, detected.platform);
    if (requestId !== previewRequestIdRef.current) return;
    if (oembed.title) preview.title = oembed.title;
    if (oembed.thumbnailUrl) preview.thumbnailUrl = oembed.thumbnailUrl;
    if (oembed.duration !== undefined) preview.duration = oembed.duration;

    setUrlPreview(preview);
    setIsPreviewLoading(false);
  }, [externalUrl]);

  const reset = useCallback(() => {
    setExternalUrl('');
    setExternalUrlError(null);
    setUrlPreview(null);
    setIsPreviewLoading(false);
  }, []);

  return {
    externalUrl,
    externalUrlError,
    urlPreview,
    isPreviewLoading,
    handleUrlChange,
    handlePreview,
    setExternalUrlError,
    reset,
  };
}
